import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { useAuth, API } from '../contexts/AuthContext';

/**
 * Hook personalizado para gestionar permisos del usuario
 * @returns {Object} { permissions, modules, hasPermission, canView, loading }
 */
export const usePermissions = () => {
  const { user, getAuthHeaders } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [modulesVisible, setModulesVisible] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    loadUserPermissions();
  }, [user]);

  const loadUserPermissions = async () => {
    if (!user) {
      setPermissions([]);
      setModulesVisible([]);
      setIsSuperAdmin(false);
      setLoading(false);
      return;
    }

    // Super admin tiene todos los permisos
    if (user.role === 'super_admin') {
      setIsSuperAdmin(true);
      setPermissions(['*']); // Wildcard para todos los permisos
      setModulesVisible(['*']); // Wildcard para todos los módulos
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/permissions/my-permissions`, {
        headers: getAuthHeaders()
      });

      setPermissions(response.data.permissions || []);
      setModulesVisible(response.data.modules_visible || []);
      setIsSuperAdmin(response.data.is_super_admin || false);
    } catch (error) {
      console.error('Error loading permissions:', error);
      setPermissions([]);
      setModulesVisible([]);
      setIsSuperAdmin(false);
    }

    setLoading(false);
  };

  /**
   * Verifica si el usuario tiene un permiso específico
   * @param {string} permission - Permiso a verificar (ej: "orders:create")
   * @returns {boolean}
   */
  const hasPermission = useCallback((permission) => {
    if (isSuperAdmin || permissions.includes('*')) return true;
    return permissions.includes(permission);
  }, [isSuperAdmin, permissions]);

  /**
   * Verifica si el usuario puede ver un módulo
   * @param {string} module - Módulo a verificar (ej: "orders", "products")
   * @returns {boolean}
   */
  const canViewModule = useCallback((module) => {
    if (isSuperAdmin || modulesVisible.includes('*')) return true;
    return modulesVisible.includes(module);
  }, [isSuperAdmin, modulesVisible]);

  /**
   * Verifica múltiples permisos (requiere al menos uno)
   * @param {Array<string>} permissionList - Lista de permisos
   * @returns {boolean}
   */
  const hasAnyPermission = useCallback((permissionList) => {
    if (isSuperAdmin || permissions.includes('*')) return true;
    return permissionList.some(perm => permissions.includes(perm));
  }, [isSuperAdmin, permissions]);

  /**
   * Verifica múltiples permisos (requiere todos)
   * @param {Array<string>} permissionList - Lista de permisos
   * @returns {boolean}
   */
  const hasAllPermissions = useCallback((permissionList) => {
    if (isSuperAdmin || permissions.includes('*')) return true;
    return permissionList.every(perm => permissions.includes(perm));
  }, [isSuperAdmin, permissions]);

  return {
    permissions,
    modulesVisible,
    hasPermission,
    canViewModule,
    hasAnyPermission,
    hasAllPermissions,
    isSuperAdmin,
    loading,
    reload: loadUserPermissions
  };
};
